import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Select,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
  Badge,
} from "antd";
import {
  ReloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  UserOutlined,
  PhoneOutlined,
  CarOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getSubscriptionRequests } from "../../api/parkpass/subscriptionRequests";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

// Status color mapping
const STATUS_COLORS = {
  submitted: "blue",
  pending: "orange",
  approved: "green",
  rejected: "red",
  under_review: "purple",
  completed: "cyan",
};

// Request type labels
const REQUEST_TYPE_LABELS = {
  first_request: "Première demande",
  renewal: "Renouvellement",
  address_change: "Changement d'adresse",
  vehicle_change: "Changement de véhicule",
  other: "Autre",
};

export default function CitizenSubscriptionRequests() {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    if (statusFilter) {
      setFilteredRequests(
        requests.filter((req) => req.status === statusFilter)
      );
    } else {
      setFilteredRequests(requests);
    }
  }, [statusFilter, requests]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) {
        params.status = statusFilter;
      }
      params.include_files = false; // Don't include files in list view

      const data = await getSubscriptionRequests(params);
      setRequests(data);
      setFilteredRequests(data);
      message.success("Demandes chargées avec succès");
    } catch (error) {
      console.error("Error fetching subscription requests:", error);

      if (error.response?.status === 401) {
        message.error("Session expirée. Veuillez vous reconnecter.");
        navigate("/login");
      } else if (error.response?.status === 403) {
        message.error("Vous n'êtes pas autorisé à accéder à cette ressource");
      } else {
        message.error(
          error.response?.data?.detail ||
            "Erreur lors du chargement des demandes"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  const handleViewDetails = (record) => {
    navigate(`/citizen/subscription-requests/${record.id}`);
  };

  // Calculate statistics
  const stats = {
    total: requests.length,
    submitted: requests.filter((r) => r.status === "submitted").length,
    approved: requests.filter((r) => r.status === "approved").length,
    rejected: requests.filter((r) => r.status === "rejected").length,
    pending: requests.filter((r) => r.status === "pending").length,
  };

  // Get unique statuses from the data
  const uniqueStatuses = [...new Set(requests.map((r) => r.status))];

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      fixed: "left",
      render: (id) => <Text strong>#{id}</Text>,
    },
    {
      title: "Date de création",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (date) => (
        <Space>
          <ClockCircleOutlined style={{ color: "#1890ff" }} />
          <Text>{dayjs(date).format("DD/MM/YYYY HH:mm")}</Text>
        </Space>
      ),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>
          {status?.toUpperCase() || "N/A"}
        </Tag>
      ),
      filters: uniqueStatuses.map((status) => ({
        text: status?.toUpperCase(),
        value: status,
      })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Prénom",
      dataIndex: "first_name",
      key: "first_name",
      width: 120,
    },
    {
      title: "Nom",
      dataIndex: "last_name",
      key: "last_name",
      width: 120,
    },
    {
      title: "Téléphone",
      dataIndex: "phone",
      key: "phone",
      width: 140,
      render: (phone) =>
        phone ? (
          <Space>
            <PhoneOutlined style={{ color: "#52c41a" }} />
            <Text>{phone}</Text>
          </Space>
        ) : (
          <Text type="secondary">N/A</Text>
        ),
    },
    {
      title: "Type de demande",
      dataIndex: "request_type",
      key: "request_type",
      width: 180,
      render: (type) => (
        <Tag color="blue">{REQUEST_TYPE_LABELS[type] || type || "N/A"}</Tag>
      ),
    },
    {
      title: "Matricule",
      dataIndex: "matricule",
      key: "matricule",
      width: 130,
      render: (matricule) =>
        matricule ? (
          <Space>
            <CarOutlined style={{ color: "#faad14" }} />
            <Text>{matricule}</Text>
          </Space>
        ) : (
          <Text type="secondary">N/A</Text>
        ),
    },
    {
      title: "Parking",
      dataIndex: "parking_name",
      key: "parking_name",
      width: 150,
      ellipsis: true,
      render: (parking) =>
        parking ? (
          <Tooltip title={parking}>
            <Space>
              <EnvironmentOutlined style={{ color: "#1890ff" }} />
              <Text ellipsis>{parking}</Text>
            </Space>
          </Tooltip>
        ) : (
          <Text type="secondary">N/A</Text>
        ),
    },
    {
      title: "Pièces jointes",
      dataIndex: "attachments",
      key: "attachments",
      width: 120,
      align: "center",
      render: (attachments) => (
        <Badge count={attachments?.length || 0} showZero>
          <FileTextOutlined style={{ fontSize: 18, color: "#1890ff" }} />
        </Badge>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          size="small"
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Title level={2}>
          <FileTextOutlined /> Demandes d'abonnement
        </Title>
        <Text type="secondary">
          Gérez les demandes d'abonnement des utilisateurs Citizen Wallet
        </Text>
      </Card>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total"
              value={stats.total}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Soumises"
              value={stats.submitted}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Approuvées"
              value={stats.approved}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Rejetées"
              value={stats.rejected}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space wrap>
            <Select
              placeholder="Filtrer par statut"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              style={{ width: 200 }}
              allowClear
            >
              {uniqueStatuses.map((status) => (
                <Option key={status} value={status}>
                  <Tag color={STATUS_COLORS[status] || "default"}>
                    {status?.toUpperCase()}
                  </Tag>
                </Option>
              ))}
            </Select>
          </Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchRequests}
            loading={loading}
          >
            Actualiser
          </Button>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredRequests}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1600 }}
          pagination={{
            total: filteredRequests.length,
            pageSize: 10,
            showTotal: (total) => `Total: ${total} demandes`,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      </Card>
    </div>
  );
}
